/**
 * Which failures the merchant has already been shown (#6).
 *
 * <p><b>Why this exists at all.</b> The sidebar's red dot is derived from the agent's CURRENT state
 * (`FAILED` / `AWAITING_APPROVAL`) — there is no "read" anywhere in that chain. So the dot stayed lit
 * after the merchant opened the conversation and read the failure, and the only thing that could
 * clear it was the agent's next run changing its state (2026-08-31 acceptance, #6). A marker that
 * cannot be dismissed by doing the thing it asks for stops being a signal.
 *
 * <p><b>Only FAILED is dismissible, deliberately.</b> "It broke" is information — once seen, it has
 * been delivered. "It is waiting for you" is an outstanding action, and letting a glance clear that
 * dot would hide a draft that still needs approving. So AWAITING_APPROVAL stays state-driven: it
 * clears when the merchant actually decides.
 *
 * <p><b>Keyed on taskId, not agentType.</b> Acknowledging a failure must not deafen the agent for
 * everything after it — the next failure is a new task id and lights the dot again.
 *
 * <p><b>localStorage, not the server.</b> The read state has one consumer and no cross-device
 * requirement worth a table and an endpoint; if that changes, this module is the single place that
 * knows where it lives. A quota error or a private-mode failure degrades to "nothing acknowledged",
 * which shows a dot that has already been read — the safe direction, since the opposite would hide a
 * real failure.
 */

const KEY = 'yepai:acked-failed-tasks';
/**
 * Fired after an ack so a mounted dot can drop immediately.
 *
 * <p>The alternative was for the page that shows the failure to call the attention hook — but that
 * hook owns a 30s poll of the roster AND the approvals queue, so a second consumer would double
 * that traffic just to write one string. Writing to storage does not re-render anything, hence the
 * event: one writer, any number of listeners, no extra polling.
 */
export const ATTENTION_ACKED_EVENT = 'yepai:attention-acked';
/** Keep the list from growing forever; a task old enough to fall off will never be reported again. */
const MAX_REMEMBERED = 200;

function read(): string[] {
  try {
    const raw = window.localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

/** The task ids whose failure the merchant has already seen. */
export function ackedTaskIds(): Set<string> {
  return new Set(read());
}

/** Remember that this failure has been shown. Newest last, oldest trimmed. */
export function ackTask(taskId: string | null | undefined): void {
  if (!taskId) return;
  try {
    const next = read().filter(id => id !== taskId);
    next.push(taskId);
    window.localStorage.setItem(KEY, JSON.stringify(next.slice(-MAX_REMEMBERED)));
  } catch {
    /* Storage unavailable — the dot stays lit, which is the harmless direction. */
  }
  // Outside the try: a failed write must still notify, or the dot would sit there for a whole
  // session because a storage quota error also swallowed the only signal that it had been read.
  window.dispatchEvent(new Event(ATTENTION_ACKED_EVENT));
}

/**
 * Forget an ack once it has been reported to the server (see useAgentAttention.convertAcks).
 *
 * <p>One-shot by design: leaving the id in storage would let the same row's NEXT failure be
 * swallowed by an ack that was already spent. No event is dispatched — the only listener is the
 * reporter that called this, and re-notifying would loop.
 */
export function consumeAck(taskId: string | null | undefined): void {
  if (!taskId) return;
  try {
    const next = read().filter(id => id !== taskId);
    window.localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* Storage unavailable — the ack lingers, which only delays a dot clearing; harmless. */
  }
}
